import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal, SUB_CONFIRM_LAYER, TextButton, useConfirmDialog } from '@safetyhub/ui';
import { adminApi, AiAnalysisListItem, AiAnalysisDetail, UnmatchedPattern, WorkClassificationSuggestion } from '../lib/api';
import { InfoRow } from '../components/InfoRow';
import { ModalCancelButton } from '../components/ModalCancelButton';

const MATCH_STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'matched', label: '지식베이스 매칭' },
  { value: 'unmatched', label: 'AI 분석만' },
  { value: 'user_created', label: '관리자 생성 HK 매칭' },
];

// ⚠️ 심각도 선택지 상수(`SEVERITY_OPTIONS` — `CRITICAL`·`HIGH`·`MEDIUM`·`LOW`)는 2026-10-07에
// **삭제했다.** 그 어휘는 서버가 내리는 config 라벨(`높음`·`상` …)과 어긋나 배선해도 한 건도
// 걸리지 않고, 심각도는 파생값이라 목록 전체를 SQL로 거를 수도 없다. 같은 enum 목록을 다시
// 만들지 말 것 — 사유와 복원 조건은 아래 심각도 select 자리의 주석이 정본이다.

/**
 * 심각도 라벨 — **`null`은 「미산출」이고 「낮음」이 아니다.**
 *
 * `ai_analysis_logs`에는 심각도 칼럼이 없어 서버가 발견사항의
 * `aiLikelihoodScore`·`aiSeverityScore`에서 shared `scoreToRiskLevel`로 파생해 내린다. 점수가
 * 없는 행이 있으므로(dev 486건 중 8건) 그때 `null`로 온다 — **「낮음」으로 떨어뜨리면 위험도를
 * 과소평가한 거짓 사실이 된다.** 「낮음」과 「모름」은 다른 사실이다.
 *
 * ⚠️ `default`가 받은 값을 **그대로 돌려주는 것은 의도다** — 서버가 내리는 값은 실제로
 * **config 라벨**(`높음`·`보통`·`낮음`·`상`·`중`·`하`)이고 아래 enum 분기에 하나도 안 걸린다.
 * 그래도 값을 숨기지 않는다. enum 분기를 남긴 것은 다른 경로가 그 축으로 바뀌었을 때의 폴백이다.
 *
 * ⚠️ **같은 목록에 어휘가 둘 섞인다**(dev 실측: `상/중/하` 104건 · `높음/보통/낮음` 250건) —
 * 그래서 색은 대개 `default`의 회색으로 떨어진다. 이 어휘 혼재가 **심각도 필터를 비활성으로 둔
 * 이유**이기도 하다(그 select 자리의 주석이 정본 — 선택지 상수는 삭제했다).
 *
 * ⚠️ 서버는 `severityLevel`·`severityScale`을 함께 내리지만 **이 화면은 일부러 안 읽는다**
 * (`matcher.service.ts` 심각도 docblock이 그 소비처 0을 사유와 함께 적어 뒀다). 지금 쓰면
 * **발동하지 않는 코드**가 된다 — dev에서 쓰이는 config가 **전부 3단계**라 모든 행이 `3`이고
 * 「높음」이 한 뜻이어서 「높음(3/3)」을 그려도 정보가 0인데, 다음 사람에게는 **「이 축은 다뤄져
 * 있다」는 거짓 안심**을 준다.
 *
 * ⚠️ **되짚을 조건 — 응답에 `severityScale`이 «둘 이상» 섞이는 순간이다.**
 * `빈도×강도 (5×5)` config가 **DB에 실재하고**(쓰는 조직만 아직 없다) 라벨이 3×3과 **완전히
 * 겹친다**(`높음`·`보통`·`낮음` 셋 다). 어느 조직이 그것을 고르면 같은 목록의 「높음」이
 * **3/3과 3/5 두 뜻**이 되고, 그것이 **화면에 아무 표시 없이** 나타난다 — 라벨이 같아서 사람이
 * 눈으로 못 가른다. 그 신호가 보이면 고칠 자리는 **여기다**: `severityScale`을 받아
 * 「높음(3/5)」류로 가르거나 tooltip에 적는다.
 * (같은 조건이 서버 docblock·줄기 P4-f 잔여에도 적혀 있다 — 세 자리가 짝이다.)
 */
function severityLabel(severity: string | null): string {
  if (severity == null) return '미산출';
  switch (severity) {
    case 'CRITICAL': return '심각';
    case 'HIGH': return '높음';
    case 'MEDIUM': return '보통';
    case 'LOW': return '낮음';
    default: return severity;
  }
}

/** 미산출(`null`)은 **색도 갈라야** 한다 — 알 수 없는 코드값(회색 700)과 같아 보이면 안 된다. */
function severityBadgeClass(severity: string | null): string {
  if (severity == null) return 'bg-gray-50 text-gray-400';
  switch (severity) {
    case 'CRITICAL': return 'bg-red-100 text-red-700';
    case 'HIGH': return 'bg-orange-100 text-orange-700';
    case 'MEDIUM': return 'bg-yellow-100 text-yellow-700';
    case 'LOW': return 'bg-blue-100 text-blue-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('ko-KR', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * 점검의 「수검업체 > 현장」 표시 — **수검업체가 없는 현장이 흔하다**(AUDITED link 미연결).
 *
 * 서버는 `site: { name, company }`를 내린다(`siteAuditedCompany` 평탄화). 수검업체가 `null`이면
 * 현장명만 쓴다 — 접두를 빈 문자열로 두면 `" > 현장명"`이 새어 나간다.
 */
function siteLabel(site: AiAnalysisDetail['inspection']['site']): string | null {
  if (!site) return null;
  return site.company?.name ? `${site.company.name} > ${site.name}` : site.name;
}

/**
 * 「사진이 지워졌다」 표지 — **타일 규칙의 소유자는 `MlPhotoThumbnail`이고 이 화면엔 타일이 없다.**
 *
 * ML 소스 화면은 썸네일 자체를 흐리고(`opacity-40 grayscale`) 그 위에 「삭제됨」을 얹는다. 이
 * 화면의 목록은 **사진 칸이 없는 로그 테이블**이라 그 규칙을 실을 carrier가 없어, 같은 사실을
 * 텍스트 표지로 싣는다. 주어(「사진」)를 밝히는 이유는 타일이 없으면 그냥 「삭제됨」이
 * **로그가 지워진 것**으로 읽히기 때문이다(`ai_analysis_logs`에는 `deleted_at`이 없다).
 *
 * ⚠️ 흐림·탈색 값은 여기서 **다시 정하지 않는다** — 그것을 복제하면 같은 개념이 두 곳에서
 * 갈린다. 상세 모달 사진에도 흐림을 주려면 `MlPhotoThumbnail`에서 클래스 상수를 먼저 뽑아야
 * 한다(그 파일 소유자에게 청할 일이다).
 *
 * ⚠️ **조건부 렌더다** — 존재가 곧 상태를 뜻하는 표지를 항상 렌더하고 문구만 비우면 그 손실이
 * spec에서 초록으로 지나간다.
 */
function PhotoDeletedBadge() {
  return (
    <span className="inline-block px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-xs font-medium">
      사진 삭제됨
    </span>
  );
}

/**
 * 「점검이 지워졌다」 표지 — 문구·색을 ML 소스 화면(`MlSources`의 현장명 접미)과 **같게** 쓴다.
 *
 * 사진 삭제와 점검 삭제는 **다른 사실**이다. 사진이 살아 있는데 「사진 삭제됨」을 붙이면 거짓이라
 * 두 표지를 따로 둔다.
 */
function InspectionDeletedMark() {
  return <span className="ml-1 text-red-500">(점검 삭제)</span>;
}

/**
 * 「발견사항이 지워졌다」 표지 — 사진 삭제와 **대개 같이 오지만 같은 사실이 아니다.**
 *
 * 사진 soft delete가 그 발견사항에도 `deletedAt`을 찍으므로(`inspection-photo.service.ts`) 둘이
 * 함께 오는 것이 보통이지만, **발견사항만 지워진 경우**가 있어 그때 「사진 삭제됨」으로 옮겨 쓰면
 * 거짓이 된다. 그래서 주어를 따로 둔다.
 */
function FindingDeletedMark() {
  return <span className="ml-1 text-red-500">(발견사항 삭제)</span>;
}

/**
 * 승격 후보(미매칭 패턴의 Finding) 한 건의 수명 표지 — **세 사실을 각각 제 주어로** 말한다.
 *
 * ⚠️ 조건부 렌더다. 하나도 없으면 `null`을 돌려 **아무것도 그리지 않는다** — 빈 표지를 항상
 * 렌더하면 그 손실이 spec에서 초록으로 지나간다.
 *
 * ⚠️ **사실 표시이고 경고가 아니다 — 그 구분이 여기서는 결정 사항이다.**
 * 「주의」·「경고」·확인 모달·`disabled`·기본 제외를 **넣지 말 것**. 결정 기록
 * `2026-10-07-photo-lifecycle-scenario-spec.md`(ⓖ)가 「**등급은 되돌릴 수 있고 삭제는 못 한다 —
 * 모집단을 미리 좁히면 나중에 못 넓힌다**」, 「**학습 단계에서 고른다**」로 정했고, 사용자 판단은
 * 「삭제 시에 보관을 하되 추적이 돼야 한다 — ML에서 활용할 예정이다」였다. 즉 **지워진 사진·맥락도
 * ML 자산**이므로 승격을 망설이게 하는 신호는 그 결정을 거스른다. 운영자에게 필요한 것은
 * 「이것이 어떤 출처인가」 하나다.
 * (초안에 있던 승격 버튼 옆 집계 경고는 그 이유로 **철회했다** — 다시 넣지 말 것.)
 *
 * ⚠️ 이 모달들엔 64px·40px 썸네일이 있어 `MlPhotoThumbnail`을 끼울 **수는** 있다. 그런데 그렇게
 * 하지 않은 이유가 둘이다: ① 같은 화면의 목록 테이블은 타일이 없어 텍스트 표지를 쓰므로, 한
 * 화면 안에서 같은 사실을 **두 언어로** 말하게 된다 ② 그 컴포넌트의 `onClick`은 이벤트를 넘기지
 * 않아 `<label>` 안에서 쓰면 **체크박스가 함께 토글된다**(현재 코드가 `e.preventDefault()`로
 * 막고 있는 동작이다). 흐림·탈색 규칙의 소유자는 여전히 `MlPhotoThumbnail` 하나다.
 */
function FindingLifecycleMarks({
  finding,
}: {
  finding: UnmatchedPattern['findings'][number];
}) {
  const { photoDeletedAt, inspectionDeletedAt, findingDeletedAt } = finding;
  if (!photoDeletedAt && !inspectionDeletedAt && !findingDeletedAt) return null;
  return (
    <span className="mt-1 block text-xs">
      {photoDeletedAt && <PhotoDeletedBadge />}
      {findingDeletedAt && <FindingDeletedMark />}
      {inspectionDeletedAt && <InspectionDeletedMark />}
    </span>
  );
}

/** 상세 모달 「점검」 칸 — 현장 표시에 수명(점검 삭제)을 덧붙인다. */
function InspectionCell({ inspection }: { inspection: AiAnalysisDetail['inspection'] }) {
  const label = siteLabel(inspection.site);
  if (!inspection.deletedAt) return <>{label ?? '-'}</>;
  return (
    <>
      {label ?? <span className="text-gray-400">현장 정보 없음</span>}
      <InspectionDeletedMark />
    </>
  );
}

type TabType = 'analysis' | 'suggestions';

export default function AiMatching() {
  const [activeTab, setActiveTab] = useState<TabType>('analysis');
  const [page, setPage] = useState(1);
  const [filterMatchStatus, setFilterMatchStatus] = useState('');
  // `filterSeverity` state는 삭제했다 — 그 select가 비활성이라 영영 빈 값이고, 남기면 죽은
  // 분기가 「곧 살아날 것」처럼 보인다(사유는 심각도 select 자리의 주석).
  const [searchQuery, setSearchQuery] = useState('');
  // 데이터 기반 필터 (4단계)
  const [filterIndustryType, setFilterIndustryType] = useState('');
  const [filterHkCategory, setFilterHkCategory] = useState('');
  const [filterWorkName, setFilterWorkName] = useState('');
  const [filterDetailWork, setFilterDetailWork] = useState('');
  const limit = 20;

  /**
   * 한 사진으로 좁히는 **URL 스코프** — ML 소스 화면의 「분석 이력」 링크가 `?photoId=`로 들어온다
   * (P4-f ⓒ-3의 받는 쪽). 다른 필터들이 `useState`인데 이것만 URL인 이유: **밖에서 들어오는
   * 입력**이라 주소에 담겨야 공유·뒤로가기가 성립한다. UI 필터를 새로 만든 것이 아니다.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const photoId = searchParams.get('photoId') || '';
  const clearPhotoScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('photoId');
    setSearchParams(next, { replace: true });
  };
  // 스코프가 바뀌면 페이지를 처음으로 — 3페이지를 보던 중 1건짜리 스코프로 들어오면 빈 표가 된다
  useEffect(() => {
    setPage(1);
  }, [photoId]);

  // 통계
  const { data: statsRes } = useQuery({
    queryKey: ['admin', 'ai-analyses', 'stats'],
    queryFn: () => adminApi.getAiAnalysisStats(),
  });
  const stats = statsRes?.data;

  // 필터 옵션 (실제 데이터 기반, 연쇄 필터)
  const { data: filterOptionsRes } = useQuery({
    queryKey: ['admin', 'ai-analyses', 'filter-options', filterIndustryType, filterHkCategory, filterWorkName],
    queryFn: () => adminApi.getAiAnalysisFilterOptions({
      industryType: filterIndustryType || undefined,
      hkCategory: filterHkCategory || undefined,
      workName: filterWorkName || undefined,
    }),
  });
  const filterOptions = filterOptionsRes?.data;
  const foIndustryTypes = useMemo(() => (filterOptions?.industryTypes || []).sort((a, b) => a.localeCompare(b, 'ko')), [filterOptions]);
  const foHkCategories = useMemo(() => (filterOptions?.hkCategories || []).sort((a, b) => a.value.localeCompare(b.value, 'ko')), [filterOptions]);
  const foWorkNames = useMemo(() => (filterOptions?.workNames || []).sort((a, b) => a.value.localeCompare(b.value, 'ko')), [filterOptions]);
  const foDetailWorks = useMemo(() => (filterOptions?.detailWorks || []).sort((a, b) => a.value.localeCompare(b.value, 'ko')), [filterOptions]);

  // 목록
  const { data: listRes, isLoading } = useQuery({
    queryKey: ['admin', 'ai-analyses', page, searchQuery, filterMatchStatus, filterIndustryType, filterHkCategory, filterWorkName, filterDetailWork, photoId],
    queryFn: () =>
      adminApi.getAiAnalysisList({
        page,
        limit,
        search: searchQuery || undefined,
        matchStatus: filterMatchStatus || undefined,
        // ⚠️ `severity`는 **보내지 않는다** — 서버가 읽지 않고, 읽게 만들어도 거를 수 없다.
        // 사유와 복원 조건은 심각도 select 자리의 주석이 정본이다. 안 읽히는 값을 계속 보내면
        // 다음 사람이 「배선돼 있다」고 읽는다.
        photoId: photoId || undefined,
        industryType: filterIndustryType || undefined,
        hkCategory: filterHkCategory || undefined,
        workName: filterWorkName || undefined,
        detailWork: filterDetailWork || undefined,
      }),
    enabled: activeTab === 'analysis',
  });
  const items = listRes?.data?.items || [];
  const total = listRes?.data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  // 미매칭 패턴 수 (탭 배지용)
  const { data: patternsRes } = useQuery({
    queryKey: ['admin', 'ai-analyses', 'unmatched-patterns'],
    queryFn: () => adminApi.getUnmatchedPatterns(),
  });
  const patternCount = patternsRes?.data?.length || 0;

  const handleFilterChange = () => {
    setPage(1);
  };

  const matchRate =
    stats && stats.totalFindings > 0
      ? Math.round((stats.withKnowledge / stats.totalFindings) * 100)
      : 0;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">AI 분석 이력</h1>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="총 분석 건수" value={stats?.totalFindings ?? '-'} color="blue" />
        <StatCard label="지식베이스 매칭" value={stats?.withKnowledge ?? '-'} color="green" />
        <StatCard label="AI 분석만" value={stats?.withoutKnowledge ?? '-'} color="yellow" />
        <StatCard label="매칭률" value={stats ? `${matchRate}%` : '-'} color="indigo" />
      </div>

      {/* 탭 */}
      <div className="border-b">
        <nav className="flex gap-4">
          <button
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'analysis'
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('analysis')}
          >
            분석 현황
          </button>
          <button
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'suggestions'
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('suggestions')}
          >
            지식베이스 보강 제안
            {patternCount > 0 && (
              <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">
                {patternCount}
              </span>
            )}
          </button>
        </nav>
      </div>

      {activeTab === 'analysis' ? (
        <>
          {/* 필터 */}
          <Card radius="lg" elevation="none" className="space-y-3">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-gray-500 mb-1">검색</label>
                <SearchBox
                  value={searchQuery}
                  onSearch={(q) => { setSearchQuery(q); setPage(1); }}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">매칭 상태</label>
                <select
                  className="border rounded px-3 py-1.5 text-sm"
                  value={filterMatchStatus}
                  onChange={(e) => { setFilterMatchStatus(e.target.value); handleFilterChange(); }}
                >
                  {MATCH_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              {/*
                심각도 필터는 **비활성이다 — 지우지 말고, 되살리지도 말 것.**

                이 select는 2026-10-07까지 `severity`를 쿼리로 보냈지만 서버는 그 값을 **읽지 않아**
                (`knowledge.controller.ts`에 `severity` 히트 0건) 고르면 아무 일도 일어나지 않았다.
                배지가 비어 있던 동안엔 죽은 필터도 함께 조용했는데, 심각도 배지를 되살리자
                **「표시가 생겼다」가 「필터가 생겼다」로 읽히는** 자리가 됐다 — 운영자가 「높음만
                보기」를 눌렀는데 전체가 그대로 나오면 그 목록을 「높음이 전부」로 읽는다.

                서버 배선은 처방이 아니다(근거는 `matcher.service.ts` 심각도 docblock의 실측):
                ① 어휘가 config마다 다르다(dev 한 목록에 `상/중/하` 104건 · `높음/보통/낮음` 250건이
                섞인다) ② 심각도는 **파생값**이라 SQL에서 거를 수 없고, 거른 척하면 `total`·쪽수가
                거짓이 된다.

                ⚠️ 종전 선택지 상수(`SEVERITY_OPTIONS` — `CRITICAL`·`HIGH`·`MEDIUM`·`LOW`)는
                **삭제했다.** 그 어휘는 서버가 내리는 config 라벨과 어긋나 **배선해도 한 건도 안
                걸린다** — 비활성 select 안에 남겨 두면 다음 사람이 「`disabled`만 떼면 된다」고
                읽는다. 되살릴 날이 오면 선택지는 **정적 목록이 아니라 데이터에서** 와야 한다
                (조직별 어휘 + 등급 수를 함께 받아야 하므로 `severityLevel`·`severityScale`이 짝이다).
              */}
              <div>
                <label className="block text-xs text-gray-500 mb-1" htmlFor="severity-filter">
                  심각도
                </label>
                <select
                  id="severity-filter"
                  className="border rounded px-3 py-1.5 text-sm bg-gray-100 text-gray-400 cursor-not-allowed"
                  defaultValue=""
                  disabled
                  aria-describedby="severity-filter-reason"
                >
                  <option value="">전체</option>
                </select>
                <p id="severity-filter-reason" className="mt-1 text-xs text-gray-400">
                  조직마다 기준 어휘가 달라 목록 전체에서 거를 수 없습니다
                </p>
              </div>
            </div>
            {/* 데이터 기반 필터 (4단계 연쇄 드롭다운) */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs text-gray-500 font-medium">분류 필터:</span>
              <select
                className="border rounded px-3 py-1.5 text-sm min-w-[120px]"
                value={filterIndustryType}
                onChange={(e) => {
                  setFilterIndustryType(e.target.value);
                  setFilterHkCategory('');
                  setFilterWorkName('');
                  setFilterDetailWork('');
                  handleFilterChange();
                }}
              >
                <option value="">산업유형 전체</option>
                {foIndustryTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <select
                className="border rounded px-3 py-1.5 text-sm min-w-[120px]"
                value={filterHkCategory}
                onChange={(e) => {
                  setFilterHkCategory(e.target.value);
                  setFilterWorkName('');
                  setFilterDetailWork('');
                  handleFilterChange();
                }}
              >
                <option value="">공종 전체</option>
                {foHkCategories.map((c) => (
                  <option key={c.value} value={c.value}>{c.value}</option>
                ))}
              </select>
              <select
                className="border rounded px-3 py-1.5 text-sm min-w-[120px]"
                value={filterWorkName}
                onChange={(e) => {
                  setFilterWorkName(e.target.value);
                  setFilterDetailWork('');
                  handleFilterChange();
                }}
              >
                <option value="">작업명 전체</option>
                {foWorkNames.map((w) => (
                  <option key={w.value} value={w.value}>{w.value}</option>
                ))}
              </select>
              <select
                className="border rounded px-3 py-1.5 text-sm min-w-[120px]"
                value={filterDetailWork}
                onChange={(e) => {
                  setFilterDetailWork(e.target.value);
                  handleFilterChange();
                }}
              >
                <option value="">소분류 전체</option>
                {foDetailWorks.map((d) => (
                  <option key={d.value} value={d.value}>{d.value}</option>
                ))}
              </select>
            </div>
          </Card>

          {/*
            좁혀진 상태를 **조건부 렌더**로 알린다 — 없으면 운영자가 「왜 1건만 보이나」를 모른다.
            해제 수단(「전체 보기」)을 같은 자리에 둬서 막히지 않게 한다.
          */}
          {photoId && (
            <Card radius="lg" elevation="none" surface="soft">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-gray-700">
                  한 사진의 분석 이력으로 좁혀진 목록입니다
                  <span className="ml-2 text-xs text-gray-400">사진 {photoId}</span>
                </span>
                <TextButton onClick={clearPhotoScope}>전체 보기</TextButton>
              </div>
            </Card>
          )}

          {/* 목록 테이블 + 상세 모달 (독립 컴포넌트 — 행 클릭 시 부모 re-render 방지) */}
          <AnalysisTable
            items={items}
            isLoading={isLoading}
            total={total}
            page={page}
            limit={limit}
            totalPages={totalPages}
            onPageChange={setPage}
            /*
             * 왜 비었는지를 말한다 — 사진으로 좁힌 0건은 **정상**이다(`ai_analysis_logs`가 사진
             * 전체를 덮지 않는다). 빈 표만 두면 「분석 로그가 없다」와 「조회가 안 됐다」가 같은
             * 모양이 된다.
             */
            emptyMessage={
              photoId
                ? '이 사진에는 AI 분석 로그가 없습니다. 모든 사진이 분석 로그를 갖는 것은 아닙니다.'
                : 'AI 분석 데이터가 없습니다.'
            }
          />

          {/* 작업명별 분석 Top 10 */}
          {stats && stats.topWorkNames.length > 0 && (
            <Card padding="none" radius="lg" elevation="none" className="overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50">
                <h2 className="text-sm font-semibold text-gray-700">작업명별 분석 건수 Top 10</h2>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">작업명</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">건수</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stats.topWorkNames.map((tw) => (
                    <tr key={tw.workName}>
                      <td className="px-4 py-2 text-sm text-gray-900">{tw.workName}</td>
                      <td className="px-4 py-2 text-sm text-right text-gray-900">{tw.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      ) : (
        <SuggestionsTab />
      )}

    </div>
  );
}

// ============================================
// HK 보강제안 탭
// ============================================

function SuggestionsTab() {
  const queryClient = useQueryClient();
  const [selectedPattern, setSelectedPattern] = useState<UnmatchedPattern | null>(null);
  const [createPattern, setCreatePattern] = useState<UnmatchedPattern | null>(null);
  const [createSelectedIds, setCreateSelectedIds] = useState<string[]>([]);
  const [createSuggestions, setCreateSuggestions] = useState<WorkClassificationSuggestion[]>([]);

  const { data: patternsRes, isLoading } = useQuery({
    queryKey: ['admin', 'ai-analyses', 'unmatched-patterns'],
    queryFn: () => adminApi.getUnmatchedPatterns(),
  });
  const patterns = patternsRes?.data || [];

  // 보강 제안 항목(패턴)에서 작업명 중심 통계 계산
  const suggestionStats = useMemo(() => {
    if (patterns.length === 0) return null;

    const totalFindings = patterns.reduce((sum, p) => sum + p.count, 0);
    const workMap = new Map<string, {
      count: number;
      industryType: string | null;
      categories: Map<string, number>;
    }>();

    for (const p of patterns) {
      const workName = p.inferredWorkName || '(미분류)';
      if (!workMap.has(workName)) {
        workMap.set(workName, { count: 0, industryType: p.industryType, categories: new Map() });
      }
      const entry = workMap.get(workName)!;
      entry.count += p.count;
      if (!entry.industryType && p.industryType) entry.industryType = p.industryType;
      entry.categories.set(p.category, (entry.categories.get(p.category) || 0) + p.count);
    }

    const byWorkName = Array.from(workMap.entries())
      .map(([name, data]) => ({
        name,
        count: data.count,
        industryType: data.industryType,
        categories: Array.from(data.categories.entries())
          .map(([catName, cnt]) => ({ name: catName, count: cnt }))
          .sort((a, b) => b.count - a.count),
      }))
      .sort((a, b) => b.count - a.count);

    return { totalFindings, byWorkName };
  }, [patterns]);

  return (
    <>
      {suggestionStats && (
        <UnmatchedStatsBlock stats={suggestionStats} />
      )}

      <Card padding="none" radius="lg" elevation="none" className="overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">
            미매칭 패턴 (유사도 클러스터링)
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            유사한 위험 설명이 반복되는 미매칭 패턴을 임베딩 기반으로 그룹화하여 표시합니다.
          </p>
        </div>

        {isLoading ? (
          <div className="px-4 py-8 text-center text-gray-400">불러오는 중...</div>
        ) : patterns.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-400">
            반복되는 미매칭 패턴이 없습니다.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {patterns.map((pattern) => (
              <div
                key={pattern.clusterId}
                className="p-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedPattern(pattern)}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900">{pattern.category}</span>
                  {pattern.subCategory && (
                    <span className="text-sm text-gray-400"> / {pattern.subCategory}</span>
                  )}
                  <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                    {pattern.count}건
                  </span>
                  {pattern.inferredWorkName && (
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                      {pattern.inferredWorkName}
                    </span>
                  )}
                </div>

                <p className="text-sm text-gray-700 line-clamp-2">
                  {pattern.representativeDescription}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {selectedPattern && (
        <PatternDetailModal
          pattern={selectedPattern}
          onClose={() => setSelectedPattern(null)}
          onCreateKnowledge={(ids, suggestions) => {
            setCreatePattern(selectedPattern);
            setCreateSelectedIds(ids);
            setCreateSuggestions(suggestions);
            setSelectedPattern(null);
          }}
        />
      )}

      {createPattern && (
        <CreateKnowledgeModal
          pattern={createPattern}
          initialSelectedIds={createSelectedIds}
          initialSuggestions={createSuggestions}
          onClose={() => setCreatePattern(null)}
          onCreated={() => {
            setCreatePattern(null);
            queryClient.invalidateQueries({ queryKey: ['admin', 'ai-analyses'] });
          }}
        />
      )}
    </>
  );
}

// ============================================
// 미매칭 패턴 상세 모달
// ============================================

function PatternDetailModal({
  pattern,
  onClose,
  onCreateKnowledge,
}: {
  pattern: UnmatchedPattern;
  onClose: () => void;
  onCreateKnowledge: (selectedIds: string[], suggestions: WorkClassificationSuggestion[]) => void;
}) {
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(pattern.findings.map((f) => f.id)),
  );

  const toggleId = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = selectedIds.size === pattern.findings.length;
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(pattern.findings.map((f) => f.id)));
  };

  // 스크롤 잠금·포커스·Esc는 base가 소유한다(종전 이 컴포넌트의 body overflow effect를 걷었다).
  // 사진 뷰어가 떠 있는 동안 Esc는 이 모달을 닫지 않는다 — 뷰어에는 Esc가 없어, 켜 두면 사진을 보다
  // Esc를 눌렀을 때 패턴 상세까지 통째로 닫힌다.
  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="미매칭 패턴 상세"
        size="xl"
        closeOnEsc={!viewerUrl}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={onClose}>
              닫기
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={selectedIds.size === 0 || loadingSuggestions}
              onClick={async () => {
                let suggestions: WorkClassificationSuggestion[] = [];
                if (pattern.inferredWorkName) {
                  setLoadingSuggestions(true);
                  try {
                    const res = await adminApi.suggestClassification({
                      workName: pattern.inferredWorkName,
                      detailWork: pattern.inferredDetailWork,
                    });
                    suggestions = res.data?.suggestions || [];
                  } catch { /* ignore */ }
                  setLoadingSuggestions(false);
                }
                onCreateKnowledge(Array.from(selectedIds), suggestions);
              }}
            >
              {loadingSuggestions ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  분류 추천 조회 중...
                </span>
              ) : (
                `HK 생성 (${selectedIds.size}건 선택)`
              )}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {/* 카테고리 */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">카테고리</label>
            <p className="text-sm text-gray-900">
              {pattern.category}
              {pattern.subCategory && <span className="text-gray-400"> / {pattern.subCategory}</span>}
            </p>
          </div>

          {/* 대표 설명 */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">대표 위험 설명</label>
            <p className="text-sm text-gray-900 bg-orange-50 rounded px-3 py-2 border border-orange-100">
              {pattern.representativeDescription}
            </p>
          </div>

          {/* 클러스터 통계 */}
          <div className="flex gap-4">
            <div className="bg-gray-50 rounded px-4 py-2 border">
              <p className="text-xs text-gray-500">유사 건수</p>
              <p className="text-lg font-bold text-orange-600">{pattern.count}건</p>
            </div>
            <div className="bg-gray-50 rounded px-4 py-2 border">
              <p className="text-xs text-gray-500">최근 발생</p>
              <p className="text-lg font-bold text-gray-700">
                {pattern.findings.length > 0 ? formatDate(pattern.findings[0].createdAt) : '-'}
              </p>
            </div>
          </div>

          {/* 포함된 Findings 목록 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-gray-500">
                포함된 Findings ({selectedIds.size}/{pattern.findings.length}건 선택)
              </label>
              <TextButton className="text-xs" onClick={toggleAll}>
                {allSelected ? '전체 해제' : '전체 선택'}
              </TextButton>
            </div>
            <div className="max-h-80 overflow-y-auto border rounded divide-y divide-gray-100">
              {pattern.findings.map((f) => (
                <label key={f.id} className="px-3 py-2.5 flex gap-3 cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="mt-5 rounded flex-shrink-0"
                    checked={selectedIds.has(f.id)}
                    onChange={() => toggleId(f.id)}
                  />
                  {/* 사진 썸네일 */}
                  {f.photoUrl ? (
                    <button
                      className="flex-shrink-0 w-16 h-16 rounded border overflow-hidden hover:ring-2 hover:ring-brand-400 transition-shadow bg-gray-100"
                      onClick={(e) => { e.preventDefault(); setViewerUrl(f.photoUrl); }}
                      title="사진 보기"
                    >
                      <img
                        src={f.photoThumbnailUrl || f.photoUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ) : (
                    <div className="flex-shrink-0 w-16 h-16 rounded border bg-gray-50 flex items-center justify-center">
                      <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-800">{f.description}</p>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(f.createdAt)}</p>
                    <FindingLifecycleMarks finding={f} />
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* 사진 뷰어 — 패턴 상세(z-[70]) 위에 겹쳐 뜨는 전체화면 뷰어라 한 층 위(z-[80])에 body로 포탈한다.
          종전엔 모달 오버레이 «안»에 `z-[60]`으로 그려져 그 쌓임 맥락 안에서만 위였다. */}
      {viewerUrl &&
        createPortal(
        // eslint-disable-next-line local/no-raw-overlay -- 전체화면 사진 뷰어(패널이 아니라 화면 자체를 점유 — RAW_OVERLAY_EXEMPT docblock의 NOT_A_MODAL 판정)
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80"
          onClick={() => setViewerUrl(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
            onClick={() => setViewerUrl(null)}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={viewerUrl}
            alt="현장 사진"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
          document.body,
        )}
    </>
  );
}

// ============================================
// HK 생성 모달
// ============================================

// export는 미저장 닫기 spec(`__tests__/formModalDirtyClose.test.tsx`)이 이 모달만 띄우기 위함이다.
export function CreateKnowledgeModal({
  pattern,
  initialSelectedIds,
  initialSuggestions,
  onClose,
  onCreated,
}: {
  pattern: UnmatchedPattern;
  initialSelectedIds?: string[];
  initialSuggestions?: WorkClassificationSuggestion[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [industryType, setIndustryType] = useState('');
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [detailCategory, setDetailCategory] = useState('');
  const [riskSituation, setRiskSituation] = useState(
    pattern.representativeDescription || '',
  );
  const [measuresText, setMeasuresText] = useState(
    (pattern.aiMeasures || []).join('\n'),
  );
  const [selectedFindingIds, setSelectedFindingIds] = useState<Set<string>>(
    new Set(initialSelectedIds || pattern.findings.map((f) => f.id)),
  );
  const [addToWC, setAddToWC] = useState(false);

  // 산업 유형 / 카테고리 / 서브카테고리 연동 목록
  const { data: dropdownRes } = useQuery({
    queryKey: ['admin', 'work-classifications', 'dropdown', industryType, category, subCategory],
    queryFn: () => adminApi.getWorkClassificationDropdownOptions({
      industryType: industryType || undefined,
      category: category || undefined,
      subCategory: subCategory || undefined,
    }),
  });
  const industryTypes = dropdownRes?.data?.industryTypes || [];
  const categoryOptions = dropdownRes?.data?.categories || [];
  const subCategoryOptions = dropdownRes?.data?.subCategories || [];
  const detailCategoryOptions = dropdownRes?.data?.detailCategories || [];

  // AI 추천 (PatternDetailModal에서 미리 조회하여 전달받음)
  const suggestions = initialSuggestions || [];
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(suggestions.length > 0);

  const applySuggestion = (s: WorkClassificationSuggestion) => {
    setIndustryType(s.industryType);
    setCategory(s.category);
    setSubCategory(s.subCategory);
    setDetailCategory(s.detailCategory || '');
    setSuggestionsExpanded(false);
  };

  // "공종-작업 분류에 추가" 체크박스 활성 조건
  const allFieldsFilled = industryType.trim() && category.trim() && subCategory.trim() && detailCategory.trim();
  const alreadyExists = useMemo(() => {
    const catExists = categoryOptions.some((c: { value: string }) => c.value === category);
    const subCatExists = subCategoryOptions.some((sc: { value: string }) => sc.value === subCategory);
    if (!catExists || !subCatExists) return false;
    // 소분류가 입력된 경우: 소분류까지 일치해야 이미 존재하는 것
    if (detailCategory.trim()) {
      return detailCategoryOptions.some((dc: { value: string }) => dc.value === detailCategory);
    }
    return true;
  }, [categoryOptions, subCategoryOptions, detailCategoryOptions, category, subCategory, detailCategory]);
  const canAddClassification = allFieldsFilled && !alreadyExists;

  // canAddClassification이 false가 되면 체크 해제
  useEffect(() => {
    if (!canAddClassification) setAddToWC(false);
  }, [canAddClassification]);

  const mutation = useMutation({
    mutationFn: () =>
      adminApi.createKnowledgeFromPattern({
        industryType: industryType || null,
        category,
        subCategory: subCategory || null,
        detailCategory: detailCategory.trim() || null,
        hazardFactor: riskSituation.substring(0, 100),
        riskSituation,
        measures: measuresText
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean),
        logIds: Array.from(selectedFindingIds),
        addToWorkClassification: addToWC,
      }),
    onSuccess: () => {
      onCreated();
    },
  });

  const toggleFinding = (id: string) => {
    setSelectedFindingIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isValid = category.trim() && subCategory.trim() && detailCategory.trim() && riskSituation.trim() && measuresText.trim();

  // 미저장 닫기 보호 — 기준선은 열림 시점의 값(state 초기값과 같은 식: 패턴 대표 문구·AI 대책 프리필 ·
  // 넘겨받은 선택). AI 추천 적용도 분류 칸을 바꾸므로 dirty로 센다.
  const [initial] = useState(() => ({
    riskSituation: pattern.representativeDescription || '',
    measuresText: (pattern.aiMeasures || []).join('\n'),
    selectedKey: [...(initialSelectedIds || pattern.findings.map((f) => f.id))].sort().join(','),
  }));
  const isDirty =
    industryType !== '' ||
    category !== '' ||
    subCategory !== '' ||
    detailCategory !== '' ||
    riskSituation !== initial.riskSituation ||
    measuresText !== initial.measuresText ||
    addToWC ||
    [...selectedFindingIds].sort().join(',') !== initial.selectedKey;

  // 헤더(제목·✕)·푸터 고정 + 본문 스크롤은 base `bodyScroll`(기본)이 맡는다. 종전 헤더에 함께
  // 고정돼 있던 「AI 분석 분류 (참고)」 블록은 base 헤더에 자리가 없어 본문 머리로 옮겼다(이제 본문과
  // 함께 스크롤된다). 생성 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다.
  return (
    <Modal
      open
      onClose={onClose}
      title="HK 지식베이스 생성"
      size="xl"
      closeOnOverlay={!mutation.isPending}
      closeOnEsc={!mutation.isPending}
      showCloseButton={!mutation.isPending}
      closeLocked={mutation.isPending}
      isDirty={isDirty}
      footer={
        <>
          <ModalCancelButton onCancel={onClose} disabled={mutation.isPending} size="sm" />
          <Button
            variant="primary"
            size="sm"
            disabled={!isValid || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? '생성 중...' : `HK 생성 및 매칭 반영 (${selectedFindingIds.size}건)`}
          </Button>
        </>
      }
    >
        <div className="space-y-5">
          {/* AI 분석 분류 참고 */}
          <div className="space-y-3">
            <div className="bg-brand-50 border border-brand-200 rounded-card px-4 py-3">
              <p className="text-xs font-medium text-brand-700 mb-1">AI 분석 분류 (참고)</p>
              <p className="text-sm text-brand-800">
                {pattern.category}{pattern.subCategory ? ` / ${pattern.subCategory}` : ''}
                {pattern.inferredWorkName && (
                  <span className="ml-2 text-purple-700">
                    [{pattern.inferredWorkName}{pattern.inferredDetailWork ? ` > ${pattern.inferredDetailWork}` : ''}]
                  </span>
                )}
              </p>
            </div>

            {/* AI 추천 분류 (접기/펼치기) */}
            {suggestions.length > 0 && (
              <div>
                <button
                  className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
                  onClick={() => setSuggestionsExpanded(!suggestionsExpanded)}
                >
                  <svg
                    className={`w-3.5 h-3.5 transition-transform ${suggestionsExpanded ? 'rotate-90' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  AI 추천 분류 ({suggestions.length}건)
                </button>
                {suggestionsExpanded && (
                  <div className="mt-1.5 space-y-1.5">
                    {suggestions.slice(0, 3).map((s) => (
                      <div
                        key={s.id}
                        className="flex items-center justify-between border rounded px-3 py-2 bg-green-50 border-green-200"
                      >
                        <span className="text-sm text-green-900">
                          {s.industryType} &gt; {s.category} &gt; {s.subCategory}
                          {s.detailCategory && <span className="text-gray-500"> &gt; {s.detailCategory}</span>}
                          <span className="ml-1 text-xs text-green-600">
                            (유사도 {(s.similarity * 100).toFixed(0)}%)
                          </span>
                        </span>
                        <button
                          className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded hover:bg-green-200"
                          onClick={() => applySuggestion(s)}
                        >
                          적용
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 분류 체계 (datalist 입력 지원) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">산업 유형</label>
              <input
                list="industryType-list"
                className="w-full border rounded px-3 py-2 text-sm"
                value={industryType}
                onChange={(e) => { setIndustryType(e.target.value); setCategory(''); setSubCategory(''); setDetailCategory(''); }}
                placeholder="선택 또는 입력"
              />
              <datalist id="industryType-list">
                {industryTypes.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">공종(대분류)</label>
              <input
                list="category-list"
                className="w-full border rounded px-3 py-2 text-sm"
                value={category}
                onChange={(e) => { setCategory(e.target.value); setSubCategory(''); setDetailCategory(''); }}
                placeholder="선택 또는 입력"
              />
              <datalist id="category-list">
                {categoryOptions.map((c) => (
                  <option key={c.value} value={c.value} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">작업명(중분류)</label>
              <input
                list="subCategory-list"
                className="w-full border rounded px-3 py-2 text-sm"
                value={subCategory}
                onChange={(e) => { setSubCategory(e.target.value); setDetailCategory(''); }}
                placeholder="선택 또는 입력"
              />
              <datalist id="subCategory-list">
                {subCategoryOptions.map((sc) => (
                  <option key={sc.value} value={sc.value} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">소분류</label>
              <input
                list="detailCategory-list"
                className="w-full border rounded px-3 py-2 text-sm"
                value={detailCategory}
                onChange={(e) => setDetailCategory(e.target.value)}
                placeholder="선택 또는 입력"
              />
              <datalist id="detailCategory-list">
                {detailCategoryOptions.map((dc) => (
                  <option key={dc.value} value={dc.value} />
                ))}
              </datalist>
            </div>
          </div>

          {/* 공종-작업 분류에 추가 체크박스 */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="addToWC"
              className="rounded"
              checked={addToWC}
              disabled={!canAddClassification}
              onChange={(e) => setAddToWC(e.target.checked)}
            />
            <label htmlFor="addToWC" className={`text-sm ${canAddClassification ? 'text-gray-700' : 'text-gray-400'}`}>
              공종-작업 분류에 추가
            </label>
            {!canAddClassification && (
              <span className="text-xs text-gray-400">
                {!allFieldsFilled
                  ? '(산업유형, 공종, 작업명, 소분류를 모두 입력해주세요)'
                  : '(이미 등록된 분류입니다)'}
              </span>
            )}
          </div>

          {/* 위험상황 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              위험상황 <span className="text-xs text-gray-400">(위험요인 자동 파생, 임베딩 검색에 사용)</span>
            </label>
            <textarea
              className="w-full border rounded px-3 py-2 text-sm resize-none"
              rows={2}
              value={riskSituation}
              onChange={(e) => setRiskSituation(e.target.value)}
              placeholder="위험상황을 입력하세요"
            />
          </div>

          {/* 개선대책 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              개선대책 <span className="text-xs text-gray-400">(한 줄에 하나씩)</span>
            </label>
            <textarea
              className="w-full border rounded px-3 py-2 text-sm resize-none"
              rows={4}
              value={measuresText}
              onChange={(e) => setMeasuresText(e.target.value)}
              placeholder="개선대책을 입력하세요&#10;한 줄에 하나씩 작성"
            />
          </div>

          {/* 관련 Findings */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              소급 매칭할 Findings ({selectedFindingIds.size}/{pattern.findings.length}건)
            </label>
            <div className="border rounded divide-y divide-gray-100">
              {pattern.findings.map((f) => (
                <label
                  key={f.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="rounded flex-shrink-0"
                    checked={selectedFindingIds.has(f.id)}
                    onChange={() => toggleFinding(f.id)}
                  />
                  {f.photoUrl ? (
                    <img
                      src={f.photoThumbnailUrl || f.photoUrl}
                      alt=""
                      className="flex-shrink-0 w-10 h-10 rounded border object-cover bg-gray-100"
                    />
                  ) : (
                    <div className="flex-shrink-0 w-10 h-10 rounded border bg-gray-50 flex items-center justify-center">
                      <svg className="w-4 h-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-700 truncate">{f.description}</p>
                    <FindingLifecycleMarks finding={f} />
                  </div>
                </label>
              ))}
            </div>
          </div>

          {mutation.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              생성 실패: {(mutation.error as Error)?.message || '알 수 없는 오류'}
            </div>
          )}
        </div>
    </Modal>
  );
}

// ============================================
// 공통 컴포넌트
// ============================================

function UnmatchedStatsBlock({ stats }: {
  stats: {
    totalFindings: number;
    byWorkName: Array<{
      name: string;
      count: number;
      industryType: string | null;
      categories: Array<{ name: string; count: number }>;
    }>;
  };
}) {
  const [open, setOpen] = useState(false);

  return (
    <Card padding="none" radius="lg" elevation="none" className="overflow-hidden mb-4">
      <button
        type="button"
        className="w-full px-4 py-3 bg-orange-50 flex items-center justify-between cursor-pointer hover:bg-orange-100 transition-colors"
        onClick={() => setOpen(v => !v)}
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-4 h-4 text-orange-500 transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <h2 className="text-sm font-semibold text-orange-800">보강 필요 현황</h2>
        </div>
        <span className="text-sm text-orange-600 font-medium">총 {stats.totalFindings}건</span>
      </button>

      {open && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
              <th className="px-4 py-2 font-medium">작업명(공종)</th>
              <th className="px-4 py-2 font-medium text-right w-16">건수</th>
              <th className="px-4 py-2 font-medium w-28">추정 산업</th>
              <th className="px-4 py-2 font-medium">주요 위험유형</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {stats.byWorkName.map((row) => (
              <tr key={row.name} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-900">{row.name}</td>
                <td className="px-4 py-2 text-right text-orange-600 font-medium">{row.count}건</td>
                <td className="px-4 py-2">
                  {row.industryType ? (
                    <span className="inline-block px-1.5 py-0.5 rounded text-xs bg-brand-50 text-brand-700">
                      {row.industryType}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">-</span>
                  )}
                </td>
                <td className="px-4 py-2 text-gray-600">
                  {row.categories.slice(0, 3).map((cat, i) => (
                    <span key={cat.name}>
                      {i > 0 && ', '}
                      {cat.name}({cat.count})
                    </span>
                  ))}
                  {row.categories.length > 3 && (
                    <span className="text-gray-400"> 외 {row.categories.length - 3}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function StatCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  const colorMap: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    green: 'bg-green-50 text-green-700',
    yellow: 'bg-yellow-50 text-yellow-700',
  };
  return (
    <Card radius="lg" elevation="none">
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${colorMap[color] || 'text-gray-900'}`}>{value}</p>
    </Card>
  );
}

function DetailModal({
  detail,
  loading,
  onClose,
  onUnmatch,
}: {
  detail: AiAnalysisDetail | null;
  loading: boolean;
  onClose: () => void;
  onUnmatch: (id: string) => void;
}) {
  // 매칭 해제 확인 — 이 상세(z-[70]) 위에 뜨므로 한 층 위(`SUB_CONFIRM_LAYER`).
  const [confirm, confirmDialog] = useConfirmDialog();
  // 스크롤 잠금·포커스·Esc는 base가 소유한다(종전 이 컴포넌트의 body overflow effect를 걷었다).
  // 폭은 base 최대(max-w-2xl)보다 넓은 종전 max-w-3xl을 `!`로 고정한다(같은 property 덧칠의 승자를
  // Tailwind 출력 순서에 맡기지 않는다).
  return (
    <Modal open onClose={onClose} title="AI 위험분석 상세" size="xl" panelClassName="!max-w-3xl">
        <div className="space-y-6">
          {loading ? (
            <div className="text-center py-12 text-gray-400">불러오는 중...</div>
          ) : !detail ? (
            <div className="text-center py-12 text-gray-400">데이터를 찾을 수 없습니다.</div>
          ) : (
            <>
              {/* AI 분석 결과 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  {detail.photo?.url ? (
                    <>
                      <img src={detail.photo.url} alt="분석 사진" className="w-full rounded-card-sm border object-cover max-h-64" />
                      {detail.photo.deletedAt && (
                        <div className="mt-1">
                          <PhotoDeletedBadge />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-48 bg-gray-100 rounded-card-sm border flex items-center justify-center text-gray-400">사진 없음</div>
                  )}
                </div>
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">AI 분석 결과</h3>
                  <InfoRow labelWidth="sm" label="카테고리" value={`${detail.category}${detail.subCategory ? ' / ' + detail.subCategory : ''}`} />
                  <InfoRow labelWidth="sm" label="위험 설명" value={detail.description} />
                  <InfoRow labelWidth="sm" label="심각도" value={
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${severityBadgeClass(detail.severity)}`}>
                      {severityLabel(detail.severity)}
                    </span>
                  } />
                  <InfoRow labelWidth="sm" label="AI 신뢰도" value={detail.aiConfidence != null ? `${(detail.aiConfidence * 100).toFixed(0)}%` : null} />
                  {detail.aiProvider && <InfoRow labelWidth="sm" label="AI 모델" value={`${detail.aiProvider}${detail.aiModel ? ' / ' + detail.aiModel : ''}`} />}
                  {(detail.inferredWorkName || detail.inferredDetailWork) && (
                    <InfoRow labelWidth="sm" label="추론 분류" value={
                      [detail.inferredWorkName, detail.inferredDetailWork].filter(Boolean).join(' / ')
                    } />
                  )}
                  <InfoRow labelWidth="sm" label="점검" value={<InspectionCell inspection={detail.inspection} />} />
                </div>
              </div>

              {/* 지식베이스 매칭 */}
              {detail.knowledgeMatched && detail.matchedKnowledge && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center justify-between">
                    지식베이스 매칭
                    {detail.matchedKnowledge.source === 'USER' && (
                      <button
                        onClick={async () => {
                          if (
                            await confirm({
                              title: '매칭 해제',
                              body: '매칭을 해제하시겠습니까?\n해제 후 이 로그는 미매칭 상태로 돌아갑니다.',
                              confirmLabel: '해제',
                              danger: true,
                              ...SUB_CONFIRM_LAYER,
                            })
                          ) {
                            onUnmatch(detail.id);
                          }
                        }}
                        className="text-xs text-red-500 hover:text-red-700 font-normal"
                      >
                        매칭 해제
                      </button>
                    )}
                  </h3>
                  <Card padding="sm" surface="soft" elevation="none">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-800">
                        {detail.matchedKnowledge.category}
                        {detail.matchedKnowledge.subCategory && <span className="text-gray-400"> / {detail.matchedKnowledge.subCategory}</span>}
                      </span>
                      {detail.matchScore != null && (
                        <span className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${
                          detail.matchScore >= 0.7 ? 'bg-green-100 text-green-700' :
                          detail.matchScore >= 0.5 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {detail.matchScore.toFixed(2)}
                        </span>
                      )}
                    </div>
                    {detail.matchedKnowledge.hazardFactor && (
                      <p className="text-xs text-gray-600 mb-1">위험요인: {detail.matchedKnowledge.hazardFactor}</p>
                    )}
                    {detail.ragReason && (
                      <p className="text-xs text-gray-400 mb-2">RAG 판단: {detail.ragReason}</p>
                    )}
                    {detail.matchedKnowledge.knowledgeMeasures.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs text-gray-500">대책:</span>
                        <ol className="list-decimal list-inside mt-1 space-y-0.5">
                          {detail.matchedKnowledge.knowledgeMeasures.map((m) => (
                            <li key={m.id} className="text-xs text-gray-700">{m.content}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </Card>
                </div>
              )}

              {/* 관련 법규 */}
              {detail.regulationLinks && (detail.regulationLinks as Array<unknown>).length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    관련 법규
                    <span className="ml-2 text-xs font-normal text-brand-600">
                      ({(detail.regulationLinks as Array<unknown>).length}건)
                    </span>
                  </h3>
                  <div className="space-y-2">
                    {(detail.regulationLinks as Array<{ type: string; article?: string; summary?: string }>).map((reg, idx) => (
                      <div key={idx} className="border rounded-card p-3 bg-brand-50/50">
                        <p className="text-sm font-medium text-gray-800">
                          [{reg.type}] {reg.article || ''}
                        </p>
                        {reg.summary && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{reg.summary}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {confirmDialog}
    </Modal>
  );
}

/**
 * 분석 테이블 + 상세 모달 (독립 컴포넌트 — 행 클릭 시 부모 re-render 방지)
 * selectedFindingId 상태를 내부에서 관리하여 모달 열기/닫기가 부모에 영향 없음
 */
function AnalysisTable({
  items,
  isLoading,
  total,
  page,
  limit,
  totalPages,
  onPageChange,
  emptyMessage,
}: {
  items: AiAnalysisListItem[];
  isLoading: boolean;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  /** 0건일 때의 문구 — **왜 비었는지**가 스코프마다 다르므로 호출부가 정한다. */
  emptyMessage: string;
}) {
  const queryClient = useQueryClient();
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);

  const { data: detailRes, isLoading: detailLoading } = useQuery({
    queryKey: ['admin', 'ai-analyses', 'detail', selectedFindingId],
    queryFn: () => adminApi.getAiAnalysisDetail(selectedFindingId!),
    enabled: !!selectedFindingId,
  });
  const detail: AiAnalysisDetail | null = detailRes?.data || null;

  const handleUnmatch = async (logId: string) => {
    try {
      await adminApi.unmatchAnalysisLog(logId);
      setSelectedFindingId(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-analyses'] });
    } catch (error) {
      alert(error instanceof Error ? error.message : '매칭 해제에 실패했습니다.');
    }
  };

  return (
    <>
      <Card padding="none" radius="lg" elevation="none" className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <colgroup>
              <col className="w-[42%]" />
              <col className="w-[22%]" />
              <col className="w-[8%]" />
              <col className="w-[7%]" />
              <col className="w-[7%]" />
              <col className="w-[14%]" />
            </colgroup>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">위험 설명</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">공종-작업 분류</th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500">심각도</th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500">매칭</th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500">법규</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">일시</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">불러오는 중...</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">{emptyMessage}</td>
                </tr>
              ) : (
                items.map((item: AiAnalysisListItem) => (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedFindingId(item.id)}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <span className="line-clamp-2">{item.description}</span>
                      {/* 수명 표지 — 지워진 사진·점검의 로그가 구별 없이 섞이는 것을 막는다(P4-f ⓒ-1) */}
                      {(item.photo?.deletedAt || item.inspection.deletedAt) && (
                        <span className="mt-1 block">
                          {item.photo?.deletedAt && <PhotoDeletedBadge />}
                          {item.inspection.deletedAt && <InspectionDeletedMark />}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <span className="line-clamp-1">
                        {item.inferredWorkName ? (
                          <>
                            {item.inferredWorkName}
                            {item.inferredDetailWork && <span className="text-gray-400"> &gt; {item.inferredDetailWork}</span>}
                          </>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-sm text-center whitespace-nowrap">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${severityBadgeClass(item.severity)}`}>
                        {severityLabel(item.severity)}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-sm text-center whitespace-nowrap">
                      {item.knowledgeMatched ? (
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                          {item.matchScore != null ? `${(item.matchScore * 100).toFixed(0)}%` : '매칭'}
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                          없음
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm text-center whitespace-nowrap">
                      {item.regulationLinks && (item.regulationLinks as Array<unknown>).length > 0 ? (
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-brand-100 text-brand-700">
                          {(item.regulationLinks as Array<unknown>).length}건
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(item.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <span className="text-sm text-gray-600">
              총 {total}건 중 {(page - 1) * limit + 1}~{Math.min(page * limit, total)}건
            </span>
            <div className="flex gap-1">
              <button
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              >
                이전
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <button
                    key={p}
                    className={`px-3 py-1 text-sm border rounded ${p === page ? 'bg-brand-600 text-white border-brand-600' : ''}`}
                    onClick={() => onPageChange(p)}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
              >
                다음
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* 상세 모달 */}
      {selectedFindingId && (
        <DetailModal
          detail={detail}
          loading={detailLoading}
          onClose={() => setSelectedFindingId(null)}
          onUnmatch={handleUnmatch}
        />
      )}
    </>
  );
}

/**
 * 검색 입력 (독립 컴포넌트 — 타이핑 시 부모 re-render 방지)
 * 내부 debounce(400ms)로 실시간 검색, 부모에는 확정된 값만 전달
 */
function SearchBox({ value, onSearch }: { value: string; onSearch: (query: string) => void }) {
  const [input, setInput] = useState(value);

  useEffect(() => {
    const trimmed = input.trim();
    if (trimmed === value) return;
    const timer = setTimeout(() => onSearch(trimmed), 400);
    return () => clearTimeout(timer);
  }, [input]);

  return (
    <div className="relative">
      <input
        type="text"
        className="w-full border rounded px-3 py-1.5 pr-7 text-sm"
        placeholder="위험 설명 검색 (띄어쓰기 = AND)"
        value={input}
        onChange={(e) => setInput(e.target.value)}
      />
      {input && (
        <button
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          onClick={() => { setInput(''); onSearch(''); }}
        >
          &times;
        </button>
      )}
    </div>
  );
}
